import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Checkbox, Descriptions, Form, Input, Select, Space, Typography, message, notification } from 'antd';
import lib from '../../../assets/utils/lib';

const { Text } = Typography;

function unwrapBody(resp) {
  return resp?.data || resp;
}

function apiErrorMessage(body, fallback) {
  if (!body) return fallback;
  const code = body.code || body.error_code;
  const msg = body.message || body.msg;
  if (code && msg) return `${msg} (${code})`;
  return msg || code || fallback;
}

export function normalizeCapabilitiesCatalog(resp) {
  const body = unwrapBody(resp);
  if (Number(body?.error) && body?.error !== 0) return [];
  const data = body?.data ?? body;
  if (Array.isArray(data?.capabilities)) return data.capabilities;
  if (Array.isArray(data?.capability_catalog)) return data.capability_catalog;
  return [];
}

/** Multi-select for create-partner modal (`GET /capabilities` or preloaded catalog). */
export function CapabilityPickerField({ value, onChange, catalog: catalogProp }) {
  const [fetched, setFetched] = useState([]);
  const [loading, setLoading] = useState(false);
  const options = catalogProp?.length ? catalogProp : fetched;

  useEffect(() => {
    if (catalogProp?.length) return undefined;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const resp = await lib.listIntegrationCapabilities();
        if (cancelled) return;
        setFetched(normalizeCapabilitiesCatalog(resp));
      } catch {
        if (!cancelled) setFetched([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [catalogProp]);

  if (!options.length && !loading) {
    return <Text type="secondary">No capabilities in catalog (optional).</Text>;
  }

  return (
    <Checkbox.Group
      value={value || []}
      onChange={onChange}
      style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
    >
      {options.map((cap) => (
        <Checkbox key={cap.key} value={cap.key} disabled={loading}>
          <Text strong>{cap.label || cap.key}</Text>
          {cap.description && (
            <div style={{ color: 'rgba(0,0,0,0.55)', fontSize: 12, marginLeft: 0 }}>{cap.description}</div>
          )}
        </Checkbox>
      ))}
    </Checkbox.Group>
  );
}

/** Detail screen: edit from `capability_catalog` + PATCH capabilities. */
export function PartnerCapabilitiesPanel({ partnerId, capabilityCatalog, onSaved }) {
  const [selected, setSelected] = useState([]);
  const [saving, setSaving] = useState(false);

  const catalog = useMemo(
    () => (Array.isArray(capabilityCatalog) ? capabilityCatalog : []),
    [capabilityCatalog],
  );

  useEffect(() => {
    setSelected(catalog.filter((c) => c.enabled).map((c) => c.key));
  }, [catalog]);

  const save = useCallback(async () => {
    setSaving(true);
    try {
      const resp = await lib.patchIntegrationPartner(partnerId, { capabilities: selected });
      const body = unwrapBody(resp);
      if (Number(body?.error) && body?.error !== 0) {
        notification.error({ message: apiErrorMessage(body, 'Could not update capabilities') });
        return;
      }
      message.success('Capabilities updated');
      onSaved?.();
    } catch (e) {
      notification.error({ message: 'Could not update capabilities', description: e?.message });
    } finally {
      setSaving(false);
    }
  }, [partnerId, selected, onSaved]);

  if (!catalog.length) return null;

  return (
    <div style={{ marginTop: 8 }}>
      <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
        Enabled capabilities control portal scopes and admin modules for this partner.
      </Text>
      <Checkbox.Group
        value={selected}
        onChange={setSelected}
        style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}
      >
        {catalog.map((cap) => (
          <Checkbox key={cap.key} value={cap.key}>
            <Text strong>{cap.label || cap.key}</Text>
            {cap.description && (
              <div style={{ color: 'rgba(0,0,0,0.55)', fontSize: 12 }}>{cap.description}</div>
            )}
          </Checkbox>
        ))}
      </Checkbox.Group>
      <Button type="primary" onClick={save} loading={saving}>
        Save capabilities
      </Button>
    </div>
  );
}

/** True when an enabled capability exposes outbound connector ops. */
export function partnerHasOutboundConnector(capabilityCatalog) {
  if (!Array.isArray(capabilityCatalog)) return false;
  return capabilityCatalog.some(
    (c) => c.enabled && Array.isArray(c.connectors) && c.connectors.includes('outbound'),
  );
}

/** Read-only preview of `portal.manifest` for ops (nav + capability flags). */
export function PortalManifestPreview({ manifest }) {
  if (!manifest || typeof manifest !== 'object') return null;
  const nav = Array.isArray(manifest.nav) ? manifest.nav : [];
  const caps = Array.isArray(manifest.capabilities) ? manifest.capabilities : [];

  return (
    <div style={{ marginTop: 8 }}>
      {manifest.has_integration_access === false && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 8 }}
          message="Partner portal will show no integration access until capabilities are assigned."
        />
      )}
      {nav.length > 0 && (
        <div style={{ marginBottom: 8 }}>
          <Text strong>Portal navigation</Text>
          <ul style={{ margin: '4px 0 0', paddingLeft: 20 }}>
            {nav.map((item) => (
              <li key={item.path || item.label}>
                <code>{item.path}</code> — {item.label}
                {item.kind === 'capability' && item.capability_key ? (
                  <Text type="secondary"> ({item.capability_key})</Text>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      )}
      {caps.length > 0 && (
        <div>
          <Text strong>Portal capabilities</Text>
          <ul style={{ margin: '4px 0 0', paddingLeft: 20 }}>
            {caps.map((c) => (
              <li key={c.key}>
                {c.label || c.key}
                {c.settings_path ? <Text type="secondary"> → {c.settings_path}</Text> : null}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/** Outbound webhook ops panel when outbound connector is assigned. */
export function PaAgentOutboundConnectorPanel({ partnerId, connector, onSaved }) {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const summary = connector?.['pa.agent']?.outbound_summary || connector?.['pa.agent']?.outbound || null;

  useEffect(() => {
    form.setFieldsValue({
      url: summary?.url || '',
      auth_type: summary?.auth_type || 'bearer',
    });
  }, [summary, form]);

  const submit = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const payload = {
        url: String(values.url).trim(),
        auth_type: values.auth_type,
      };
      if (values.outbound_api_key?.trim()) {
        payload.outbound_api_key = values.outbound_api_key.trim();
      }
      const resp = await lib.patchPaAgentOutboundConnector(partnerId, payload);
      const body = unwrapBody(resp);
      if (Number(body?.error) && body?.error !== 0) {
        notification.error({ message: apiErrorMessage(body, 'Could not save connector') });
        return;
      }
      message.success('Outbound connector updated');
      form.setFieldValue('outbound_api_key', undefined);
      onSaved?.();
    } catch (e) {
      if (e?.errorFields) return;
      notification.error({ message: 'Could not save connector', description: e?.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ marginTop: 16 }}>
      <Descriptions size="small" column={1} bordered style={{ marginBottom: 12 }}>
        <Descriptions.Item label="Configured">{summary?.configured ? 'Yes' : 'No'}</Descriptions.Item>
        <Descriptions.Item label="Auth type">{summary?.auth_type || '—'}</Descriptions.Item>
        <Descriptions.Item label="Secret on file">{summary?.has_secret ? 'Yes' : 'No'}</Descriptions.Item>
      </Descriptions>
      <Form form={form} layout="vertical">
        <Form.Item name="url" label="Webhook URL" rules={[{ required: true, message: 'URL is required' }]}>
          <Input placeholder="https://agent.example/hooks/pa" />
        </Form.Item>
        <Form.Item name="auth_type" label="Auth type" rules={[{ required: true }]}>
          <Select
            options={[
              { value: 'bearer', label: 'Bearer' },
              { value: 'x_api_key', label: 'X-Api-Key' },
            ]}
          />
        </Form.Item>
        <Form.Item name="outbound_api_key" label="Outbound API key (write-only)">
          <Input.Password placeholder="Leave blank to keep existing secret" autoComplete="new-password" />
        </Form.Item>
        <Button type="primary" onClick={submit} loading={saving}>
          Save outbound connector
        </Button>
      </Form>
    </div>
  );
}
